// GAPP document renderer — fills the ORIGINAL DCH appendix PDFs.
//
// Every document in a request renders on its own (renderGappDocument): a
// state form is the blank original (public/forms/gapp/*.pdf) with the typed
// values, X-marks and signatures drawn on top with pdf-lib at the coordinates
// in gappFieldMaps.js; AHFC's own two documents (PHI Release, Assessment
// signature page) come from @react-pdf (GappInternalPages.js); an uploaded
// document (form_key "upload:…", an LON or anything under "Other") is the file
// as uploaded with the fields the sender placed on it drawn in. Each one is
// filed to Drive as its own PDF — DCH gets each appendix standalone, never a
// merged packet. renderGappPacket concatenates them plus the signature
// certificate, and exists for the on-screen preview only.
//
// Spanish packets (data.lang === 'es'): AHFC's own pages render in Spanish and
// every state form for which DCH publishes a Spanish original is filled on that
// original — declared as `esFile` (+ optional `esItems`) on its field map. An
// appendix with no Spanish original stays on the official English form, which
// is the version the state accepts; nothing is inserted in front of it.
//
// Input is the assembled packet data from generatePacketPdf.assemblePacketData.
// This module has no React dependency: the caller injects `renderInternal`
// (kind → PDF Blob/bytes) and, outside the browser, `loadBytes` (path → bytes),
// which lets scripts/verify-gapp-overlay.mjs render the state forms in Node.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { GAPP_FIELD_MAPS, INTERNAL_FORMS } from './gappFieldMaps.js';

const BASE = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
// Everything CAERA writes onto a state form is black, values and signatures
// alike, like a pen would be (decided 2026-09-20).
const INK = rgb(0, 0, 0);
const SIG_INK = rgb(0, 0, 0);
const TEXT_SIZE = 10;
const SIG_SIZE = 13;

// ── asset cache (originals + signature font) ─────────────────────────────────
const cache = new Map();
async function fetchBytes(path) {
  if (!cache.has(path)) {
    cache.set(path, (async () => {
      const res = await fetch(`${BASE}${path}`);
      if (!res.ok) throw new Error(`Could not load ${path} (${res.status})`);
      return new Uint8Array(await res.arrayBuffer());
    })());
  }
  return cache.get(path);
}

// ── text helpers ─────────────────────────────────────────────────────────────
function fitSize(font, text, maxW, size, min = 6) {
  let s = size;
  while (s > min && font.widthOfTextAtSize(text, s) > maxW) s -= 0.5;
  return s;
}

function truncate(font, text, maxW, size) {
  if (font.widthOfTextAtSize(text, size) <= maxW) return text;
  let t = text;
  while (t.length > 1 && font.widthOfTextAtSize(`${t}…`, size) > maxW) t = t.slice(0, -1);
  return `${t}…`;
}

// Greedy word wrap into at most `rows.length` lines of the given widths.
function wrapRows(font, text, rows, size) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const out = [];
  let i = 0;
  for (const row of rows) {
    let line = '';
    while (i < words.length) {
      const next = line ? `${line} ${words[i]}` : words[i];
      if (font.widthOfTextAtSize(next, size) > row.w) break;
      line = next; i += 1;
    }
    if (!line && i < words.length) { line = truncate(font, words[i], row.w, size); i += 1; }
    out.push(line);
    if (i >= words.length) break;
  }
  if (i < words.length && out.length) out[out.length - 1] = truncate(font, `${out[out.length - 1]} …`, rows[out.length - 1].w, size);
  return out;
}

function drawFitted(page, font, text, { x, y, w, size = TEXT_SIZE, color = INK, minSize = 7 }) {
  const t = String(text ?? '').trim();
  if (!t) return;
  let s = fitSize(font, t, w, size, minSize);
  const shown = truncate(font, t, w, s);
  page.drawText(shown, { x, y: y + 2.5, size: s, font, color });
}

function drawSignature(page, font, name, { x, y, w, size = SIG_SIZE }) {
  const t = String(name ?? '').trim();
  if (!t) return;
  const s = fitSize(font, t, w, size, 8);
  page.drawText(t, { x, y: y + 1.5, size: s, font, color: SIG_INK });
}

function drawMark(page, font, x, y) {
  page.drawText('X', { x: x - 3.5, y: y + 2, size: 11, font, color: INK });
}

// A pen tick inside a box — the LON's agree / disagree is marked with a tick
// under the word, which is how the office has always marked it.
function drawTick(page, { x, y, w, h }) {
  const s = Math.min(w, h);
  const ox = x + (w - s) / 2, oy = y + (h - s) / 2;
  const opts = { thickness: Math.max(1, s * 0.13), color: INK };
  page.drawLine({ start: { x: ox + s * 0.12, y: oy + s * 0.5 }, end: { x: ox + s * 0.4, y: oy + s * 0.18 }, ...opts });
  page.drawLine({ start: { x: ox + s * 0.4, y: oy + s * 0.18 }, end: { x: ox + s * 0.92, y: oy + s * 0.9 }, ...opts });
}

// ── data context ─────────────────────────────────────────────────────────────
function makeCtx(data) {
  const sig = data.signatures || {};
  return {
    c: data.client || {},
    f: data.forms || {},
    formData: data.formData || {},   // per form_key, for uploaded documents' placed fields
    files: data.files || {},         // Drive file id → bytes, for uploaded documents
    s: (k) => sig[k] || {},
    packetDate: data.dateShort || '',
  };
}

// ── uploaded documents: fields placed by the sender ──────────────────────────
// A placed field is { kind, party, page, x, y, w, h } with x/y/w/h as fractions
// of the page (y measured from the TOP, as the placer shows it). The slot a
// party signs on an uploaded document is keyed "<form_key>:<party>". A
// 'choice' field also carries { group, value } — one box of a question the
// signer answers, such as the LON's agree / disagree (lib/documents/lonLayout).
export const uploadSlotKey = (formKey, party) => `${formKey}:${party}`;

function applyPlacedFields(formKey, fields, pages, ctx, fonts) {
  const { helv, helvBold, cursive } = fonts;
  for (const fl of fields || []) {
    const page = pages[fl.page || 0];
    if (!page) continue;
    const { width: W, height: H } = page.getSize();
    const x = fl.x * W, w = fl.w * W, h = fl.h * H;
    const y = H - fl.y * H - h;                       // bottom-left origin
    const slot = ctx.s(uploadSlotKey(formKey, fl.party));
    switch (fl.kind) {
      case 'sig':
        drawSignature(page, cursive, slot.name, { x: x + 3, y: y + h * 0.25, w: w - 6, size: Math.min(SIG_SIZE + 4, h * 0.7) });
        break;
      case 'date':
        drawFitted(page, helv, slot.dateShort, { x: x + 3, y: y + h * 0.25, w: w - 6, size: Math.min(TEXT_SIZE, h * 0.6) });
        break;
      case 'name':
        drawFitted(page, helv, slot.name, { x: x + 3, y: y + h * 0.25, w: w - 6, size: Math.min(TEXT_SIZE, h * 0.6) });
        break;
      case 'initials': {
        const ini = String(slot.name || '').split(/\s+/).filter(Boolean).map((p) => p[0].toUpperCase()).join('');
        drawSignature(page, cursive, ini, { x: x + 3, y: y + h * 0.25, w: w - 6, size: Math.min(SIG_SIZE, h * 0.7) });
        break;
      }
      case 'check':
        if (slot.name) drawMark(page, helvBold, x + w / 2, y + h / 2 - 5);
        break;
      // One of a group the signer answers (the LON's agree / disagree): only
      // the box matching their answer is ticked, and only once they have signed.
      case 'choice':
        if (slot.name && fl.group && (slot.choices || {})[fl.group] === fl.value) drawTick(page, { x, y, w, h });
        break;
      default:
        break;
    }
  }
}

// Draw one field map onto the pages just copied for that form.
function applyMap(map, pages, ctx, fonts) {
  const { helv, helvBold, cursive } = fonts;
  for (const it of map.items) {
    const page = pages[it.page];
    if (!page) continue;
    switch (it.kind) {
      case 'text':
        drawFitted(page, helv, it.get(ctx), it);
        break;
      case 'lines': {
        const val = String(it.get(ctx) ?? '').trim();
        if (!val) break;
        const size = it.size || TEXT_SIZE;
        wrapRows(helv, val, it.rows, size).forEach((line, i) => {
          const row = it.rows[i];
          page.drawText(line, { x: row.x, y: row.y + 2.5, size, font: helv, color: INK });
        });
        break;
      }
      case 'sig':
        drawSignature(page, cursive, ctx.s(it.slot).name, it);
        break;
      case 'sdate':
        drawFitted(page, helv, ctx.s(it.slot).dateShort, it);
        break;
      case 'sname':
        drawFitted(page, helv, ctx.s(it.slot).name, it);
        break;
      case 'mark':
        if (it.when(ctx)) drawMark(page, helvBold, it.x, it.y);
        break;
      default:
        break;
    }
  }
}

// Seizure log table: 6 rows per copy of the page; extra copies for overflow.
async function applyTable(map, out, srcDoc, firstPages, ctx, fonts) {
  const { helv } = fonts;
  const rows = Array.isArray(ctx.f[map.table.rowsKey]) ? ctx.f[map.table.rowsKey] : [];
  const { cols, rowTops, rowBottom, keys } = map.table;
  const perPage = rowTops.length;
  const copies = Math.max(1, Math.ceil(rows.length / perPage));
  const pageSets = [firstPages];
  for (let k = 1; k < copies; k++) {
    const [extra] = await out.copyPages(srcDoc, [0]);
    out.addPage(extra);
    applyMap(map, [extra], ctx, fonts);      // repeat name / age header
    pageSets.push([extra]);
  }
  rows.forEach((row, ri) => {
    const page = pageSets[Math.floor(ri / perPage)][0];
    const r = ri % perPage;
    const top = rowTops[r];
    const bottom = r + 1 < rowTops.length ? rowTops[r + 1] : rowBottom;
    keys.forEach((key, ci) => {
      const val = String(row[key] ?? '').trim();
      if (!val) return;
      const cellX = cols[ci] + 3, cellW = cols[ci + 1] - cols[ci] - 6;
      const size = 7.5, lead = 9;
      const maxLines = Math.max(1, Math.floor((top - bottom - 4) / lead));
      const lines = wrapRows(helv, val, Array.from({ length: maxLines }, () => ({ w: cellW })), size);
      lines.forEach((line, li) => {
        page.drawText(line, { x: cellX, y: top - 10 - li * lead, size, font: helv, color: INK });
      });
    });
  });
}

// ── public API ───────────────────────────────────────────────────────────────
async function makeFonts(out, loadBytes) {
  out.registerFontkit(fontkit);
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const helvBold = await out.embedFont(StandardFonts.HelveticaBold);
  const cursive = await out.embedFont(await loadBytes('/fonts/DancingScript.ttf'), { subset: true });
  return { helv, helvBold, cursive };
}

function stampMeta(out, data, title) {
  out.setTitle(`${data.client?.name || 'Client'} — ${title}`);
  out.setProducer('CAERA — A Heart For Care');
  out.setCreator('CAERA');
}

export const isUploadForm = (key) => String(key || '').startsWith('upload:');

// Render ONE document of a request into its own PDF. Returns a Uint8Array, or
// null when the key is unknown (nothing to render).
export async function renderGappDocument(data, key, { loadBytes = fetchBytes, renderInternal = null } = {}) {
  const ctx = makeCtx(data);

  if (INTERNAL_FORMS.has(key)) {
    if (!renderInternal) return null;
    const r = await renderInternal(key);
    if (!r) return null;
    return r instanceof Uint8Array ? r : new Uint8Array(await r.arrayBuffer());
  }

  const out = await PDFDocument.create();
  const fonts = await makeFonts(out, loadBytes);

  if (isUploadForm(key)) {
    const fd = ctx.formData[key] || {};
    const bytes = ctx.files[fd.sourceFileId];
    if (!bytes) return null;             // file not available to this caller yet
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const pages = await out.copyPages(src, src.getPageIndices());
    pages.forEach((p) => out.addPage(p));
    applyPlacedFields(key, fd.fields, pages, ctx, fonts);
    stampMeta(out, data, fd.name || key);
    return out.save();
  }

  const map = GAPP_FIELD_MAPS[key];
  if (!map) return null;
  const es = data.lang === 'es';
  // Official Spanish state form when DCH publishes one; otherwise the official
  // English original, unchanged.
  const useEsOriginal = es && !!map.esFile;
  const file = useEsOriginal ? `es/${map.esFile}` : map.file;
  const src = await PDFDocument.load(await loadBytes(`/forms/gapp/${file}`));
  const pages = await out.copyPages(src, src.getPageIndices());
  pages.forEach((p) => out.addPage(p));
  const active = useEsOriginal && map.esItems ? { ...map, items: map.esItems } : map;
  applyMap(active, pages, ctx, fonts);
  if (active.table) await applyTable(active, out, src, pages, ctx, fonts);
  stampMeta(out, data, key);
  return out.save();
}

// Where a party's signatures land on one document, in PDF points (origin
// bottom-left, US Letter 612×792), so a signing page can highlight them over
// the rendered page. Uploaded documents come from their placed fields; state
// forms from the field map (the Spanish map when the request is Spanish);
// AHFC's own typeset pages carry no map and return nothing.
export function signatureRects(data, key, slotKeys) {
  const want = new Set(slotKeys || []);
  const out = [];
  if (isUploadForm(key)) {
    const fd = (data.formData || {})[key] || {};
    (fd.fields || []).filter((f) => f.kind === 'sig' && want.has(uploadSlotKey(key, f.party))).forEach((f) => {
      out.push({ slot: uploadSlotKey(key, f.party), page: f.page || 0, x: f.x * 612, y: 792 - (f.y + f.h) * 792, w: f.w * 612, h: f.h * 792 });
    });
    return out;
  }
  const map = GAPP_FIELD_MAPS[key];
  if (!map) return out;
  const es = data.lang === 'es';
  const items = es && map.esFile && map.esItems ? map.esItems : map.items;
  items.filter((it) => it.kind === 'sig' && want.has(it.slot)).forEach((it) => {
    out.push({ slot: it.slot, page: it.page, x: it.x, y: it.y, w: it.w, h: 18 });
  });
  return out;
}

// The signature certificate for a whole request — one file, separate from
// the documents it certifies.
export async function renderGappCertificate(data, { renderInternal = null } = {}) {
  if (!renderInternal) return null;
  const r = await renderInternal('certificate');
  if (!r) return null;
  return r instanceof Uint8Array ? r : new Uint8Array(await r.arrayBuffer());
}

// All documents + certificate in one PDF, in request order. PREVIEW ONLY —
// what a signer reads on screen and what the builder shows; never filed.
// formKeys: request form order (e.g. forms.map(f => f.form_key)).
export async function renderGappPacket(data, formKeys, opts = {}) {
  const out = await PDFDocument.create();
  stampMeta(out, data, data.packet?.label || 'GAPP documents');
  for (const key of formKeys) {
    const bytes = await renderGappDocument(data, key, opts);
    if (bytes) await appendPdf(out, bytes);
  }
  const cert = await renderGappCertificate(data, opts);
  if (cert) await appendPdf(out, cert);
  return out.save();
}

async function appendPdf(out, bytes) {
  const src = await PDFDocument.load(bytes);
  const pages = await out.copyPages(src, src.getPageIndices());
  pages.forEach((p) => out.addPage(p));
}

